import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Link } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { getChildOrderStatus } from "../../../api/bseApi";

/**
 * Parse BSE Child Order Status response (pipe-separated rows)
 * Expected: ParentOrderNo|ChildOrderNo|SchemeName|TransactionType|Amount|Units|Status|ExecDate|Remarks
 */
function parseChildOrderStatusResponse(rawData) {
  if (!rawData || typeof rawData !== "string") return [];
  const rows = rawData.split(/\r?\n/).filter((r) => r && r.trim());
  const childOrders = [];
  for (const row of rows) {
    const parts = row.split("|").map((p) => (p || "").trim());
    const upper = row.toUpperCase();
    let status = "UNKNOWN";
    if (upper.includes("EXECUTED") || upper.includes("ALLOTTED")) status = "EXECUTED";
    else if (upper.includes("ACCEPTED") || upper.includes("PENDING")) status = "ACCEPTED";
    else if (upper.includes("REJECTED") || upper.includes("REJECT")) status = "REJECTED";

    childOrders.push({
      parentOrderNo: parts[0] || "-",
      childOrderNo: parts[1] || "-",
      schemeName: parts[2] || "-",
      transactionType: parts[3] || "Purchase",
      amount: parts[4] || "",
      units: parts[5] || "",
      status,
      execDate: parts[7] || parts[6] || "",
      remarks: parts[8] || parts[7] || parts[6] || "",
    });
  }
  return childOrders;
}

/**
 * ChildOrderStatusScreen - BSE StAR MF Child Order Status (MFAPI Flag 05)
 * Opens from Order Status screen when order has child orders, or from Reports/Tracking
 * Receives: Parent OrderNo (mandatory), ClientCode (mandatory)
 */
export default function ChildOrderStatusScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = useParams();
  const state = location.state;
  const [context, setContext] = useState(null);
  const [childOrders, setChildOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [manualOrderNo, setManualOrderNo] = useState("");

  const clientCode = state?.clientCode || new URLSearchParams(location.search).get("clientCode");
  const orderNo = state?.orderNo || new URLSearchParams(location.search).get("orderNo");

  const fetchChildOrders = useCallback(async () => {
    if (!context?.orderNo || !context?.clientCode) return null;
    try {
      const res = await getChildOrderStatus({
        orderNo: context.orderNo,
        clientCode: context.clientCode,
      });
      if (res.data?.status === 1) {
        const parsed = parseChildOrderStatusResponse(res.data?.data);
        return parsed;
      }
      return null;
    } catch (err) {
      console.warn("Child order status fetch failed:", err);
      return null;
    }
  }, [context?.orderNo, context?.clientCode]);

  useEffect(() => {
    const o = (orderNo || "").toString().trim();
    const c = (clientCode || "").toString().trim();
    if (o && c) {
      setContext({ orderNo: o, clientCode: c });
      setFetching(false);
      return;
    }
    if (c && !o) {
      setContext({ orderNo: null, clientCode: c });
      setFetching(false);
      return;
    }
    if (userId && o && !c) {
      const loadUser = async () => {
        try {
          const res = await axios.get(
            `${process.env.REACT_APP_BACKEND_BASE_URL || "http://localhost:8000/api"}/user/${userId}`
          );
          const user = res.data?.status === 1 ? res.data?.data : null;
          if (!user) {
            Swal.fire("Error", "User not found.", "error").then(() => navigate("/user"));
            return;
          }
          const ucc = user.ucc ?? user.UCC ?? "";
          if (!ucc || String(ucc).trim() === "" || String(ucc) === "Reject") {
            Swal.fire("Error", "ClientCode (UCC) not available.", "error").then(() => navigate("/user"));
            return;
          }
          setContext({ orderNo: o, clientCode: String(ucc).trim() });
        } catch (err) {
          Swal.fire("Error", err.response?.data?.error || "Failed to load user.", "error").then(() => navigate("/user"));
        } finally {
          setFetching(false);
        }
      };
      loadUser();
      return;
    }
    if (!o || !c) {
      setError("OrderNo and ClientCode are required. Open from Order Status or Reports.");
      setFetching(false);
    }
  }, [orderNo, clientCode, userId, navigate]);

  useEffect(() => {
    if (!context?.clientCode) return;
    if (!context?.orderNo) {
      setLoading(false);
      setChildOrders([]);
      setError(null);
      return;
    }

    const load = async () => {
      setLoading(true);
      setError(null);
      const result = await fetchChildOrders();
      setLoading(false);
      if (result && Array.isArray(result)) {
        setChildOrders(result);
        if (result.length === 0) setError("No child orders available for this order.");
      } else {
        setError("Could not fetch child order status.");
      }
    };

    load();
  }, [context?.orderNo, context?.clientCode, fetchChildOrders]);

  const handleManualFetch = () => {
    const o = (manualOrderNo || "").toString().trim();
    if (!o) return;
    setContext((prev) => (prev ? { ...prev, orderNo: o } : { orderNo: o, clientCode: clientCode || "" }));
  };

  const handleRefresh = async () => {
    if (!context) return;
    setLoading(true);
    const result = await fetchChildOrders();
    setLoading(false);
    if (result && Array.isArray(result)) {
      setChildOrders(result);
      setError(result.length === 0 ? "No child orders available for this order." : null);
    }
  };

  const renderStatusBadge = (status) => {
    const s = (status || "").toUpperCase();
    if (s === "EXECUTED" || s === "ALLOTTED") {
      return (
        <span className="badge bg-success fs-6">
          <i className="fas fa-check-circle me-1" /> Success
        </span>
      );
    }
    if (s === "ACCEPTED") {
      return (
        <span className="badge bg-info fs-6">
          <i className="fas fa-clock me-1" /> Awaiting execution
        </span>
      );
    }
    if (s === "REJECTED") {
      return (
        <span className="badge bg-danger fs-6">
          <i className="fas fa-times-circle me-1" /> Rejected
        </span>
      );
    }
    return <span className="badge bg-secondary fs-6">{status}</span>;
  };

  if (fetching || (!context && !error)) {
    return (
      <div className="main-content">
        <div className="page-wrapper page-settings">
          <div className="content p-4">
            <div className="text-center">
              <span className="spinner-border spinner-border-sm me-2" role="status" />
              Loading...
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error && !context) {
    return (
      <div className="main-content">
        <div className="page-wrapper page-settings">
          <div className="content p-4">
            <div className="text-center">
              <i className="fas fa-exclamation-triangle text-warning fa-3x mb-3" />
              <h5>OrderNo and ClientCode Required</h5>
              <p className="text-muted mb-3">{error}</p>
              <Link to="/user/order-status" className="btn btn-primary">Back to Order Status</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Has clientCode but no orderNo - show form to enter order number (Reports/Tracking flow)
  if (context?.clientCode && !context?.orderNo) {
    return (
      <div className="main-content">
        <div className="page-wrapper page-settings">
          <div className="content">
            <div className="mb-4" style={{ background: "#ffffff", borderRadius: "8px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)", padding: "20px" }}>
              <nav aria-label="breadcrumb" style={{ marginBottom: "15px" }}>
                <ol className="breadcrumb">
                  <li className="breadcrumb-item"><Link to="/dashboard">Dashboard</Link></li>
                  <li className="breadcrumb-item"><Link to="/user">Users</Link></li>
                  <li className="breadcrumb-item"><Link to="/user/order-status" state={{ clientCode: context.clientCode }}>Order Status</Link></li>
                  <li className="breadcrumb-item active">Child Order Status</li>
                </ol>
              </nav>
              <h5>Child Order Status</h5>
              <p className="text-muted mb-3">Client: <strong>{context.clientCode}</strong>. Enter parent order number to fetch child orders.</p>
              <div className="d-flex gap-2 align-items-center flex-wrap">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Parent Order Number"
                  value={manualOrderNo}
                  onChange={(e) => setManualOrderNo(e.target.value)}
                  style={{ maxWidth: "220px" }}
                />
                <button className="btn btn-primary" onClick={handleManualFetch} disabled={!manualOrderNo.trim()}>
                  Fetch Child Orders
                </button>
                <Link to="/user/order-status" state={{ clientCode: context.clientCode }} className="btn btn-outline-secondary">Back to Order Status</Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="page-wrapper page-settings">
        <div className="content">
          <div
            className="mb-4"
            style={{
              background: "#ffffff",
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              padding: "20px",
            }}
          >
            <nav aria-label="breadcrumb" style={{ marginBottom: "15px" }}>
              <ol className="breadcrumb">
                <li className="breadcrumb-item"><Link to="/dashboard">Dashboard</Link></li>
                <li className="breadcrumb-item"><Link to="/user">Users</Link></li>
                <li className="breadcrumb-item"><Link to="/user/order-status" state={{ clientCode: context?.clientCode }}>Order Status</Link></li>
                <li className="breadcrumb-item active">Child Order Status</li>
              </ol>
            </nav>
            <h5>Child Order Status</h5>
            <p className="text-muted mb-0">
              Parent Order: <strong>{context?.orderNo}</strong> | Client: <strong>{context?.clientCode}</strong>
            </p>
          </div>

          <div
            className="mb-4"
            style={{
              background: "#ffffff",
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              padding: "24px",
              border: "1px solid #e6eef0",
            }}
          >
            <nav className="d-flex justify-content-between align-items-center mb-4">
              <h6 className="mb-0" style={{ color: "#0b6f68", fontWeight: "600" }}>
                Child Orders List
              </h6>
              <button className="btn btn-outline-primary btn-sm" onClick={handleRefresh} disabled={loading}>
                {loading ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="fas fa-sync-alt me-1" />}
                Refresh
              </button>
            </nav>

            {loading && childOrders.length === 0 && (
              <div className="text-center py-5">
                <span className="spinner-border spinner-border-lg text-primary mb-3" role="status" />
                <p>Fetching child order status...</p>
              </div>
            )}

            {error && childOrders.length === 0 && !loading && (
              <div className="alert alert-warning">
                <i className="fas fa-info-circle me-2" />
                {error}
                <div className="mt-3">
                  <button className="btn btn-outline-primary btn-sm me-2" onClick={handleRefresh}>Retry</button>
                  <Link to="/user/order-status" state={{ clientCode: context?.clientCode }} className="btn btn-outline-secondary btn-sm">Back to Order Status</Link>
                </div>
              </div>
            )}

            {childOrders.length > 0 && (
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>Parent Order</th>
                      <th>Child Order</th>
                      <th>Scheme</th>
                      <th>Type</th>
                      <th>Amount</th>
                      <th>Units</th>
                      <th>Status</th>
                      <th>Exec / NAV Date</th>
                      <th>BSE / AMC Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {childOrders.map((co, i) => (
                      <tr key={co.childOrderNo + i}>
                        <td>{co.parentOrderNo}</td>
                        <td>{co.childOrderNo}</td>
                        <td>{co.schemeName}</td>
                        <td>{co.transactionType}</td>
                        <td>{co.amount ? `₹${Number(co.amount).toLocaleString()}` : "-"}</td>
                        <td>{co.units || "-"}</td>
                        <td>{renderStatusBadge(co.status)}</td>
                        <td>{co.execDate || "-"}</td>
                        <td className="small">{co.remarks || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {childOrders.length > 0 && (
              <div className="alert alert-light border mt-3">
                <h6 className="mb-2">Status Legend</h6>
                <p className="mb-0 small">
                  <strong>ACCEPTED:</strong> Awaiting execution &nbsp;|&nbsp;
                  <strong>EXECUTED:</strong> Success – child order completed &nbsp;|&nbsp;
                  <strong>REJECTED:</strong> See remarks for reason
                </p>
              </div>
            )}

            <div className="d-flex gap-2 mt-4 pt-3" style={{ borderTop: "1px solid #e6eef0" }}>
              <Link to="/user/order-status" state={{ clientCode: context?.clientCode }} className="btn btn-outline-primary">Order Status</Link>
              <Link to="/user/allotment-statement" state={{ clientCode: context?.clientCode }} className="btn btn-outline-primary">Allotment Statement</Link>
              <Link to="/user/redemption-statement" state={{ clientCode: context?.clientCode }} className="btn btn-outline-primary">Redemption Statement</Link>
              <Link to="/user" className="btn btn-secondary">Back to Users</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
